import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, HostListener, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	BaseComponent,
	ButtonCollapseComponent,
	ButtonComponent,
	CollapseComponent,
	DividerComponent,
	FooterActionsComponent,
	IconComponent,
	InfoGroupComponent,
	ISelect,
	LinkComponent,
	ModalComponent,
	ProcessComponent,
	QrComponent,
	SelectComponent,
	TagComponent,
	TileComponent,
	ToggleAcceptComponent,
	TooltipComponent,
	TopnavComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { IToggleAccept } from '@tecalisdev/infinity/lib/interfaces/toggleAccept';
import { ReactiveFormsService } from 'src/app/core/helpers/reactive-forms.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { ScreenshotService } from 'src/app/core/services/screenshot.service';
import { SnackbarService } from 'src/app/core/services/snackbar.service';
import { IMaster } from 'src/app/shared/models/master.models';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import {
	CONNECTION_TYPE,
	DOCS_TYPE,
	EVALUATION_TYPE,
	IChooseOperation,
	IDocumentsDocs,
	IDocumentsOperation,
	IHomeOperation,
	LocalizedText,
	IOcrOperation,
	IOperation,
	IPolicyCheck,
	IProcessConfig,
	OPERATION_TYPE,
	IProcessDesign,
	HomeLog,
} from '../../models/user-identification';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { HomeService } from '../../services/home/home.service';
import { ProcessService } from '../../services/process.service';
import {
	getOcrAllowedDniText as resolveOcrAllowedDniText,
	isDniForOtherCountriesInOcr,
	isDniForSpainInOcr,
	isPassportForOtherCountriesInOcr,
	isPassportForSpainInOcr,
	isSpanishCompany as isSpanishCompanyCountry,
} from '../../utils/ocr-allowed-dni-label.util';
import { isEudiOnlyOcrConfig } from '../../utils/eudi-help.util';
import { resolveDniPhotoSideAsset } from '../../utils/dni-wireframe-photo-side.util';
import { ModalInstructionsComponent } from '../docs/modals/modal-instructions.component';
import { ModalConnectionsInstructionsComponent } from '../connections/modals/modal-connections-instructions/modal-connections-instructions.component';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-home',
	imports: [
		TranslatePipe,
		ReactiveFormsModule,
		AsyncPipe,
		NgTemplateOutlet,
		NgClass,
		AlertComponent,
		BaseComponent,
		ButtonCollapseComponent,
		ButtonComponent,
		CollapseComponent,
		DividerComponent,
		FooterActionsComponent,
		IconComponent,
		InfoGroupComponent,
		LinkComponent,
		ModalComponent,
		ProcessComponent,
		QrComponent,
		TagComponent,
		TileComponent,
		ToggleAcceptComponent,
		TopnavComponent,
		WatermarkComponent,
		SelectComponent,
		TooltipComponent,
		ModalInstructionsComponent,
		ModalConnectionsInstructionsComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './home.component.html',
	styleUrls: ['./home.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class HomeComponent implements OnInit {
	@Input()
	get operations(): IOperation[] {
		return this._operations;
	}
	set operations(operations: IOperation[]) {
		this._operations = operations;
		this.operationsWithDocuments = this.getOperationsWithDocuments();
		this.onlyPolOperation = this.isOnlyPol();

		// Inicializar con el primer colapso que deba estar expandido (operación no completada)
		if (this.expandedCollapseType === null) {
			const firstIncompleteOperation = operations.find((op) => !op.completed && op.type !== OPERATION_TYPE.HOME);
			if (firstIncompleteOperation) {
				this.expandedCollapseType = firstIncompleteOperation.type;
			}
		}
	}
	@Input()
	get operation(): IHomeOperation {
		return this._operation;
	}
	set operation(operation: IHomeOperation) {
		this._operation = operation;
		this.setControlsConditions();
	}

	@HostListener('window:resize', ['$event'])
	onResize(event: Event) {
		this.screenWidth = (event.target as Window).innerWidth;
	}
	private _operation: IHomeOperation;
	private _operations: IOperation[];
	public screenWidth: number = window.innerWidth;
	public operationsWithDocuments: IOperation[];
	public onlyPolOperation: boolean = false;
	public operationType = OPERATION_TYPE;
	public CONNECTION_TYPE = CONNECTION_TYPE;
	public EVALUATION_TYPE = EVALUATION_TYPE;
	public isSensitivePersonalDataVisible: boolean = false;
	public isAllowedDocs: {
		visible: boolean;
		type: 'dni' | 'passport' | null;
		dniScope?: 'european' | 'spain';
		operation: IOcrOperation | null;
	} = {
		visible: false,
		type: null,
		operation: null,
	};
	public formConditions: FormGroup = new FormGroup({});
	public languageForm!: FormGroup;
	public options: { [key: string]: IToggleAccept[] } = {};
	public expanded: boolean = true;
	public expandedCollapseType: OPERATION_TYPE | null = null;
	public isVisibleDocInstructions: boolean = false;
	public isVisibleConnectionInstructions: boolean = false;
	public selectedDoc: IDocumentsDocs;
	public selectedConnectionType: CONNECTION_TYPE;
	private generalConfig: IProcessConfig;
	private initOperation: boolean = false;
	private isSendingPolicyChecks: boolean = false;
	private readonly destroyRef = inject(DestroyRef);
	private readonly languageService = inject(LanguageService);
	private readonly cloudLogsService = inject(CloudLogsService);
	private readonly loadingService = inject(LoadingService);

	constructor(
		public snackbarService: SnackbarService,
		public reactiveFormService: ReactiveFormsService,
		public designService: DesignService,
		public configService: ConfigService,
		public auth: AuthService,
		public userAgentService: UserAgentService,
		private homeService: HomeService,
		private errorService: ErrorService,
		private screenShotService: ScreenshotService,
		private trans: TranslateService,
		private processService: ProcessService,
		private masterService: MasterService,
	) {
		this.configService.getConfig().subscribe((config: IProcessConfig) => {
			if (config) {
				this.generalConfig = config;
				if (!config.allowed_device?.includes('mobile')) this.expanded = false;
			}
		});

		this.languageForm = new FormGroup({
			locale: new FormControl(this.languageService.getLanguage(), {
				nonNullable: true,
				validators: [Validators.required, this.allowedLanguageValidator()],
			}),
		});
		const localeControl = this.languageForm.get('locale')!;
		// tec-select emite el ISelect completo, no el id; normalizamos a código de idioma.
		localeControl.valueChanges
			.pipe(
				map((raw) => this.normalizeLocaleValue(raw)),
				distinctUntilChanged(),
				filter((code): code is string => !!code && this.masterService.isAllowedLanguageCode(code)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((code) => {
				this.languageService.setLanguage(code);
				const raw = localeControl.value;
				if (typeof raw === 'object' && raw !== null && 'id' in raw) {
					localeControl.patchValue(code, { emitEvent: false });
				}
			});

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			const code = this.languageService.getLanguage();
			if (this.normalizeLocaleValue(localeControl.value) !== code) {
				localeControl.patchValue(code, { emitEvent: false });
			}
			this.refreshToggleOptionsLabels();
		});
	}

	/** tec-select (Infinity) propaga ISelect; el FormControl puede llevar string (id) u objeto. */
	private normalizeLocaleValue(raw: unknown): string | null {
		if (raw == null || raw === '') {
			return null;
		}
		if (typeof raw === 'object' && raw !== null && 'id' in raw) {
			return String((raw as ISelect).id);
		}
		return String(raw);
	}

	get languageSelectOptions(): ISelect[] {
		return this.masterService.getLanguages().map((opt) => ({
			id: opt.code,
			label: this.trans.instant(opt.labelKey),
		}));
	}

	private allowedLanguageValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const code = this.normalizeLocaleValue(control.value);
			if (code == null || code === '') {
				return null;
			}
			return this.masterService.isAllowedLanguageCode(code) ? null : { allowedLanguage: true };
		};
	}

	ngOnInit(): void {
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
	}

	public nextOperation(scrollDown: boolean = false): void {
		if (
			this.formConditions.valid &&
			(this.generalConfig.location !== 'mandatory' ||
				(this.generalConfig.location === 'mandatory' && this.operation.config.isLocationSaved))
		) {
			this.sendPolicyChecks();
		} else {
			if (scrollDown) {
				setTimeout(() => {
					const container = document.querySelector('.content__container') as HTMLElement | null;
					const footerEl =
						document.querySelector('tec-footer-actions') || document.querySelector('.footer-actions');
					const footerHeight = footerEl ? (footerEl as HTMLElement).clientHeight : 110;
					if (container && container.scrollHeight > container.clientHeight) {
						container.scroll({ top: container.scrollHeight - footerHeight, behavior: 'smooth' });
					} else {
						window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
					}
				}, 80);
			}
			this.formConditions.markAllAsTouched();
			setTimeout(() => {
				this.formConditions.markAsUntouched();
			}, 3000);
		}
	}

	private sendPolicyChecks(): void {
		if (this.isSendingPolicyChecks) {
			return;
		}
		this.isSendingPolicyChecks = true;
		this.showLoading(this.trans.instant('GENERAL.LOADING.POLICY_CHECKS'));

		this.homeService.sendPolicyChecks(this.getPolicyChecksBody()).subscribe({
			next: () => {
				this.operation.completed = true;
			},
			error: (error: HttpErrorResponse) => {
				this.hideLoading();
				this.isSendingPolicyChecks = false;
				// Error general
				if (this.errorService.hasGeneralError({ ...error?.error })) {
					this.screenShotService.stopSaveScreenshot();
					return;
				}
				this.cloudLogsService.sendCloudLogs(
					{
						error_code: error?.error?.error?.error_code ?? null,
						msg: error?.error?.error?.msg ?? error?.message ?? null,
						checks_sent: this.getPolicyChecksBody(),
					},
					HomeLog.POLICY_CHECKS_ERROR,
					LogType.Error,
				);
			},
			complete: () => {
				this.hideLoading();
				this.isSendingPolicyChecks = false;
			},
		});
	}

	private showLoading(text = ''): void {
		this.operation.config.iframe ? this.loadingService.showIframe(text) : this.loadingService.show(text);
	}

	private hideLoading(): void {
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	private getPolicyChecksBody(): Array<{ uuid: string; checked: boolean }> {
		const result: Array<{ uuid: string; checked: boolean }> = [];
		const raw = this.formConditions.getRawValue();
		for (const key in raw) {
			result.push({ uuid: key, checked: raw[key] ?? false });
		}
		return result;
	}

	public setControlsConditions(): void {
		if (this.operation.config.tecalis_policy_check) {
			this.formConditions.addControl('tecalis_default_check_1', new FormControl(null, Validators.requiredTrue));
			this.options = {
				...this.options,
				tecalis_default_check_1: this.buildYesNoOptions(),
			};
		}
		if (this.operation.config?.policy_checks?.length) {
			this.operation.config.policy_checks.forEach((policy: IPolicyCheck) => {
				this.formConditions.addControl(
					policy.uuid?.toString(),
					new FormControl(null, policy.required ? Validators.requiredTrue : null),
				);
				this.options = {
					...this.options,
					[policy.uuid?.toString()]: this.buildYesNoOptions(),
				};
			});
		}
	}

	private refreshToggleOptionsLabels(): void {
		if (!this.operation?.config) return;

		const nextOptions = { ...this.options };
		if (this.operation.config.tecalis_policy_check && nextOptions['tecalis_default_check_1']) {
			nextOptions['tecalis_default_check_1'] = this.buildYesNoOptions();
		}
		this.operation.config?.policy_checks?.forEach((policy: IPolicyCheck) => {
			const key = policy.uuid?.toString();
			if (key && nextOptions[key]) {
				nextOptions[key] = this.buildYesNoOptions();
			}
		});
		this.options = nextOptions;
	}

	private buildYesNoOptions(): IToggleAccept[] {
		return [
			{ label: this.trans.instant('GENERAL.BUTTON.NO'), id: 'no', value: false },
			{ label: this.trans.instant('GENERAL.BUTTON.YES'), id: 'yes', value: false },
		];
	}

	public changeValue(id: string, options: IToggleAccept[]) {
		const found = options.find((opt: IToggleAccept) => opt.value);
		if (found) {
			this.formConditions.get(id?.toString()).patchValue(found.id === 'yes' ? true : false);
		} else {
			this.formConditions.get(id?.toString()).patchValue(null);
		}
	}

	public getOperationsWithDocuments(): Array<IOperation> {
		return this.operations.filter(
			(operation: IOperation) =>
				operation.type === OPERATION_TYPE.OCR || operation.type === OPERATION_TYPE.DOCUMENTS,
		);
	}

	private isOnlyPol(): boolean {
		return (
			Boolean(this.operations.find((operation: IOperation) => operation.type === OPERATION_TYPE.POL)) &&
			!this.getOperationsWithDocuments()?.length
		);
	}

	public hasError(id: string): boolean {
		if (!id) return false;
		return (
			this.formConditions.get(id.toString()) &&
			this.formConditions.get(id.toString()).hasError('required') &&
			this.formConditions.get(id.toString()).touched
		);
	}

	/**
	 * Normaliza HTML de políticas de cliente: espacios no separadores impiden saltos de línea y rompen el layout en flex.
	 */
	public normalizePolicyLabelHtml(html: LocalizedText | null | undefined): string {
		const resolvedHtml = this.resolveText(html);
		if (!resolvedHtml) return '';
		return resolvedHtml.replace(/\u00A0/g, ' ').replace(/&nbsp;/gi, ' ');
	}

	get isMdSize(): boolean {
		return this.screenWidth <= 768;
	}

	public openDocInstructionsModal(doc: IDocumentsDocs): void {
		// Crear una copia del documento para no modificar el original
		this.selectedDoc = { ...doc };

		// Si no tiene instrucciones, asignar las instrucciones por defecto
		if (!this.selectedDoc.instructions) {
			const defaultInstructions = this.getDocumentInstructions(doc);
			if (defaultInstructions) this.selectedDoc.instructions = defaultInstructions;
		}

		this.isVisibleDocInstructions = true;
	}

	/**
	 * Obtiene las instrucciones para un documento específico.
	 * Si el documento no tiene instrucciones personalizadas, retorna las instrucciones por defecto según el tipo.
	 *
	 * @param doc El documento del cual obtener las instrucciones.
	 * @returns Las instrucciones del documento o null si no existen.
	 */
	public getDocumentInstructions(doc: IDocumentsDocs): string | null {
		// Si ya tiene instrucciones personalizadas, retornarlas
		const resolvedInstructions = this.resolveText(doc?.instructions);
		if (resolvedInstructions) return resolvedInstructions;

		// Si no existe instrucciones, buscar instrucciones por defecto (si existen)
		const instructionsKey = 'DOCUMENTS.INSTRUCTIONS_DEFAULT.' + doc.type.toUpperCase();
		const instructions = this.trans.instant(instructionsKey);

		// Solo retornar si la traducción existe (si no existe, instant devuelve la misma key)
		if (instructions && instructions !== instructionsKey) return instructions;

		return null;
	}

	/**
	 * Verifica si un documento tiene instrucciones disponibles (personalizadas o por defecto).
	 *
	 * @param doc El documento a verificar.
	 * @returns True si el documento tiene instrucciones disponibles.
	 */
	public hasDocumentInstructions(doc: IDocumentsDocs): boolean {
		return this.getDocumentInstructions(doc) !== null;
	}

	public getDocumentsStepText(operation: IDocumentsOperation): string {
		const docs: Array<IDocumentsDocs> = operation.config?.docs;

		// Si no hay exactamente un documento, mostrar texto genérico
		if (docs?.length !== 1) return this.trans.instant('HOME.DOCUMENTS_STEP');

		const doc: IDocumentsDocs = docs[0];

		const documentName = this.resolveText(doc.title) || this.trans.instant(`DOCUMENTS.TYPES.${doc.type}`);

		return this.trans.instant('HOME.DOCUMENTS_STEP_SINGLE', {
			documentName: doc.type === 'DRIVE_LICENSE' ? documentName.toLowerCase() : documentName,
		});
	}

	public getOcrAllowedDniText(operation?: IOcrOperation | null): string {
		if (!operation) {
			return this.trans.instant('OCR.GENERAL.DOCUMENT_TYPE.DOCUMENT_ID');
		}

		if (this.isSpanishCompany() && this.isDniForOtherCountries(operation)) {
			return this.trans.instant('OCR.GENERAL.DOCUMENT_TYPE.DNI_EUROPEAN');
		}

		return resolveOcrAllowedDniText(this.trans, operation, this.generalConfig?.company_country);
	}

	public isSpanishCompany(): boolean {
		return isSpanishCompanyCountry(this.generalConfig?.company_country);
	}

	/** NIF-960: workflow solo EUDI Wallet (sin docs físicos). */
	public isEudiOnlyOcr(operation: IOcrOperation): boolean {
		return isEudiOnlyOcrConfig(operation?.config);
	}

	/** NIF-960: EUDI activo en perfil y entorno permitido (INT/local). */
	public isEudiWalletEnabled(operation: IOcrOperation): boolean {
		return (
			environment.eudiWalletAllowed === true && !!operation?.config?.eudi_wallet_enabled
		);
	}

	public isDniForOtherCountries(operation: IOcrOperation): boolean {
		return isDniForOtherCountriesInOcr(operation);
	}

	public isDniForSpain(operation: IOcrOperation): boolean {
		return isDniForSpainInOcr(operation);
	}

	public isPassportForOtherCountries(operation: IOcrOperation): boolean {
		return isPassportForOtherCountriesInOcr(operation);
	}

	public isPassportForSpain(operation: IOcrOperation): boolean {
		return isPassportForSpainInOcr(operation);
	}

	public getDniPhotoSideAsset(assetPath: string): string {
		return resolveDniPhotoSideAsset(
			assetPath,
			this.isAllowedDocs?.operation?.config?.dni_wireframe_photo_side,
			false,
		);
	}

	public getAllowedDocsModalTitle(): string {
		if (this.isAllowedDocs.type === 'passport') {
			return this.trans.instant('OCR.GENERAL.DOCUMENT_TYPE.PASSPORT');
		}
		if (this.isAllowedDocs.type === 'dni' && this.isSpanishCompany()) {
			if (this.isAllowedDocs.dniScope === 'spain') {
				return this.trans.instant('DOCUMENTS.TYPES.DNI');
			}
			return this.trans.instant('OCR.GENERAL.DOCUMENT_TYPE.DNI_EUROPEAN');
		}
		if (this.isAllowedDocs.type === 'dni') {
			return this.trans.instant('OCR.GENERAL.DOCUMENT_TYPE.DOCUMENT_ID');
		}
		return '';
	}

	public resolveText(value?: LocalizedText | null): string {
		return this.languageService.resolveLocalizedText(value);
	}

	/** Título junto al logo: solo con logo Tecalis (sin logo personalizado) y si está activado en diseño. */
	public getTopnavProductTitle(design: IProcessDesign | null | undefined): string {
		if (!design?.show_product_name) {
			return '';
		}
		const t = design.title?.trim();
		return t ? t : 'Identity';
	}

	public getHomeSubtitle(): string {
		const description = this.operation?.config?.description;
		if (description && typeof description === 'object') {
			const current = this.languageService.getLanguage()?.toLowerCase();
			const byCurrent = description[current];
			if (byCurrent) return byCurrent;
			return this.trans.instant('HOME.SUBTITLE');
		}
		return this.resolveText(description) || this.trans.instant('HOME.SUBTITLE');
	}

	public toggleCollapse(operationType: OPERATION_TYPE, expanded?: boolean): void {
		this.expandedCollapseType = !expanded ? null : operationType;
	}

	public getChooseInfo(chooseOperation: IChooseOperation): {
		img: 'home-connection-bank' | 'choose-docs';
		label: string;
	} {
		const connectionWithEval: boolean = chooseOperation.config.operations.some(
			(op: IOperation) =>
				op.type === OPERATION_TYPE.CONNECTIONS &&
				(op.config.type === CONNECTION_TYPE.BANK_SCORING || op.config.evaluation),
		);
		const connectionWithoutEval: boolean = chooseOperation.config.operations.some(
			(op: IOperation) =>
				op.type === OPERATION_TYPE.CONNECTIONS &&
				!(op.config.type === CONNECTION_TYPE.BANK_SCORING || op.config.evaluation),
		);

		if (connectionWithEval || connectionWithoutEval)
			return {
				img: connectionWithEval && !connectionWithoutEval ? 'home-connection-bank' : 'choose-docs',
				label:
					connectionWithEval && !connectionWithoutEval
						? this.trans.instant('HOME.CHOOSE_ONLY_EVALUATION')
						: this.trans.instant('HOME.CHOOSE_ONLY_CONNECTION'),
			};

		return { img: 'choose-docs', label: this.trans.instant('HOME.CHOOSE_MULTIPLE') };
	}

	public areAllDocsRequired(operation: IOperation): boolean {
		if (operation.type === OPERATION_TYPE.DOCUMENTS) {
			return (operation as IDocumentsOperation).config.docs.every((doc) => doc.required);
		}
		return false;
	}

	public hasAttorneyPowersLikeDocs(): boolean {
		return Boolean(
			this.operations.find(
				(op: IOperation) =>
					op.type === OPERATION_TYPE.DOCUMENTS &&
					op.config.docs.find((doc: IDocumentsDocs) => doc.type === DOCS_TYPE.SCRIPTURE),
			) as IDocumentsOperation,
		);
	}
}
